import React from 'react';
import classnames from 'classnames';
import WarningIcon from '@mui/icons-material/Warning';
import CheckIcon from '@mui/icons-material/Check';
import slugify from 'slugify';
import {
  Input as MuiInput,
  InputAdornment,
  CircularProgress,
} from '@mui/material';

import { withStyles } from '@mui/styles';
import { noop } from 'lodash';
import _config from '../../themes/_base/_config';

type Status = 'error' | 'check' | 'loading';

// Callers flag state with the strings 'true'/'false'; error wins over check, check over loading.
export function getStatus({
  prop,
  check,
  loading,
}: Readonly<{ prop?: string; check?: string; loading?: string }>):
  | Status
  | undefined {
  if (prop === 'true') return 'error';
  if (check === 'true') return 'check';
  if (loading === 'true') return 'loading';
  return undefined;
}

const TextField = ({
  classes,
  label,
  error,
  helperText,
  inputProps = {},
  labelProps = {},
  containerProps = {},
  containerClass,
  isShowing = true,
  tooltip,
  required,
  type,
  disabled,
  onBlurHandler = noop,
  onFocusHandler = noop,
  placeholder,
  maxlength,
  minlength,
  ...props
}: any) => {
  if (!isShowing) {
    return null;
  }

  const id = props.id || `text-filed-${slugify(label)}`;

  const status = getStatus(props);
  const look =
    status &&
    {
      error: {
        input: classes.input,
        adornment: classes.adornmentRootError,
        // @ts-expect-error TS(2769): No overload matches this call.
        icon: <WarningIcon className={classes.icon} tabIndex="-1" />,
      },
      check: {
        input: classes.checkInput,
        adornment: classes.adornmentRootCheck,
        // @ts-expect-error TS(2769): No overload matches this call.
        icon: <CheckIcon className={classes.icon} tabIndex="-1" />,
      },
      loading: {
        input: '',
        adornment: classes.adornmentRootLoading,
        icon: (
          <CircularProgress
            classes={{ root: `${classes.loadingIconRoot}` }}
            color="inherit"
          />
        ),
      },
    }[status];

  const inputEle = (
    <MuiInput
      className={`${classes.inputWrapper}  ${look?.input ?? ''}`}
      required={required}
      id={id}
      disabled={disabled}
      type={type}
      maxlength={maxlength}
      minlength={minlength}
      {...props}
      inputProps={{ 'data-qa': `TEXT_INPUT:${props.name}`, ...inputProps }}
      error={Boolean(error)}
      onBlur={onBlurHandler}
      onFocus={onFocusHandler}
      placeholder={placeholder || ''}
      endAdornment={
        <InputAdornment
          classes={look ? { root: look.adornment } : null}
          position="end"
          className={classes.adornmentContainer}
        >
          {look?.icon}
        </InputAdornment>
      }
    />
  );

  return (
    <div
      className={classnames(classes.container, containerClass)}
      {...containerProps}
    >
      {label && (
        <label className={classes.label} htmlFor={id} {...labelProps}>
          {label}
          {required ? (
            <span
              style={{ color: '#f95f53', fontSize: '16px', lineHeight: '16px' }}
            >
              *
            </span>
          ) : (
            ''
          )}
          {tooltip && (
            <img
              style={{ width: 15, height: 15, marginBottom: -4 }}
              alt="icons"
              src="images/Tooltip.svg"
              title={tooltip}
            />
          )}
        </label>
      )}
      <div>
        {inputEle}
        {error && <span className={classes.errorText}>{error}</span>}
      </div>
    </div>
  );
};

const styles = ({ palette, config }: any) => {
  return {
    adornmentContainer: {
      margin: 0,
    },
    inputWrapper: {
      '& input': {
        // 'padding-right': '40px',
        'border-radius': '5px',
        border: `solid 0.8px ${config.colors.almostWhite}`,
      },

      // '& input:focus': {
      //   background: _config.colors.darkBlue,
      // },
    },
    text: {
      '& input': {
        'padding-right': '40px',
        'border-radius': '5px',
        border: `solid 0.8px ${config.colors.almostWhite}`,
        paddingLeft: 35,
        fontSize: 15,
      },
      '& input:focus': {
        background: _config.colors.darkBlue,
      },
    },
    adornmentRoot: {
      height: 'inherit',
      position: 'absolute',
      top: '50%',
      right: 0,
      transform: 'translateY(-50%)',
    },
    adornmentRootError: {
      height: 'inherit',
      position: 'absolute',
      top: '50%',
      right: 10,
      color: config.colors.textRed,
      transform: 'translateY(-50%)',
    },
    adornmentRootCheck: {
      height: 'inherit',
      position: 'absolute',
      top: '50%',
      right: 10,
      color: config.colors.cowbellAccent,
      transform: 'translateY(-50%)',
    },
    adornmentRootLoading: {
      position: 'absolute',
      top: '50%',
      right: 10,
      color: config.colors.cowbellAccent,
      transform: 'translateY(-50%)',
    },
    icon: {
      width: '20px',
      height: '30px',
    },
    checkBox: {
      width: '20px',
      height: '36px',
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: 20,
      position: 'relative',
      width: '100%',
    },

    label: {
      paddingBottom: 10,
      fontSize: config.textSizes.normal,
      fontFamily: config.fonts.stackSans,
      lineHeight: '16px',
      marginLeft: '10px',
      color: palette.text.text8,
    },

    input: {
      border: '0.1px solid #f95f53 !important',
    },

    checkInput: {
      // border:'1px solid #6ac52a !important',
    },
    helperText: {
      marginTop: 5,
      fontSize: '0.875rem',
      fontStyle: 'italic',
    },

    loadingIconRoot: {
      width: '20px !important',
      height: '20px !important',
    },

    errorText: {
      display: 'block',
      color: palette.error.main,
      fontWeight: 500,
    },
  };
};

// @ts-expect-error TS(2345): Argument of type '({ palette, config }: any) => { ... Remove this comment to see the full error message
export default withStyles(styles)(TextField);
